import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Camera,
    Upload,
    Loader2,
    UserCheck,
    CheckCircle2,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";

const BATCH_SIZE = 50;

export default function PhotosUploadPage() {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [lastSummary, setLastSummary] = useState(null);
    const fileInputRef = useRef(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [p, e] = await Promise.all([
                api.get("/photos/pending"),
                api.get("/employees"),
            ]);
            setItems(p.data);
            setEmployees(e.data);
        } catch (err) {
            toast.error("Errore nel caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const empOptions = useMemo(() => {
        return employees.map((e) => ({
            id: e.id,
            label: `${e.cognome} ${e.nome} · matr. ${e.matricola}`,
            cessato: !!(e.data_cessazione && String(e.data_cessazione).trim()),
            hasPhoto: !!e.photo,
        }));
    }, [employees]);

    const handleBulkUpload = async (filesList) => {
        if (!filesList || filesList.length === 0) return;
        const allFiles = Array.from(filesList);
        const totalBatches = Math.ceil(allFiles.length / BATCH_SIZE);
        setUploadBusy(true);
        setLastSummary(null);
        setUploadProgress({ done: 0, total: allFiles.length, batches: totalBatches, batchIdx: 0, errors: 0 });

        let totAssigned = 0, totPending = 0, totSkipped = 0, totErrors = 0;
        const errorSamples = [];
        for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
            const batch = allFiles.slice(i, i + BATCH_SIZE);
            const batchIdx = Math.floor(i / BATCH_SIZE) + 1;
            const fd = new FormData();
            for (const f of batch) fd.append("files", f);
            try {
                const { data } = await api.post("/photos/bulk", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 600000,
                });
                totAssigned += data.assigned || 0;
                totPending += data.pending || 0;
                totSkipped += data.skipped || 0;
                totErrors += data.errors || 0;
                if (data.items) {
                    errorSamples.push(...data.items.filter((i) => i.status === "error").slice(0, 3));
                }
            } catch (e) {
                totErrors += batch.length;
                const detail = e.response?.data?.detail || e.message || "errore";
                errorSamples.push({ filename: `Lotto ${batchIdx}`, error: detail });
            }
            setUploadProgress({
                done: Math.min(i + BATCH_SIZE, allFiles.length),
                total: allFiles.length,
                batches: totalBatches,
                batchIdx,
                errors: totErrors,
            });
        }

        const summary = { assigned: totAssigned, pending: totPending, skipped: totSkipped, errors: totErrors };
        setLastSummary(summary);
        const msgParts = [];
        if (totAssigned) msgParts.push(`${totAssigned} assegnate`);
        if (totPending) msgParts.push(`${totPending} in attesa`);
        if (totErrors) msgParts.push(`${totErrors} errori`);
        if (totErrors > 0) toast.warning(msgParts.join(" · "));
        else toast.success(msgParts.join(" · ") || "Caricamento completato");

        if (errorSamples.length) console.error("Photo errors:", errorSamples);

        await fetchAll();
        if (fileInputRef.current) fileInputRef.current.value = "";
        setUploadBusy(false);
        setUploadProgress(null);
    };

    const confirmAssign = async (photo, employeeId) => {
        setBusyId(photo.id);
        try {
            await api.post(`/photos/pending/${photo.id}/assign`, { employee_id: employeeId });
            toast.success("Foto assegnata");
            setItems((prev) => prev.filter((p) => p.id !== photo.id));
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore assegnazione");
        } finally {
            setBusyId(null);
        }
    };

    const deletePending = async (photo) => {
        if (!window.confirm(`Eliminare la foto "${photo.filename}"?`)) return;
        setBusyId(photo.id);
        try {
            await api.delete(`/photos/pending/${photo.id}`);
            toast.success("Foto eliminata");
            setItems((prev) => prev.filter((p) => p.id !== photo.id));
        } catch (e) {
            toast.error("Errore eliminazione");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="photos-page">
            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Camera className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-[#376DB3]">Foto tesserini</h1>
                        <p className="text-sm text-slate-600">
                            Carica le foto dei dipendenti per il tesserino. Vengono ridimensionate a 600×800 px,
                            ottimizzate in JPEG e abbinate per cognome (o cognome+nome) dal nome del file.
                        </p>
                    </div>
                </div>

                {/* Bulk upload card */}
                <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="photos-upload-card">
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex-1 min-w-[260px]">
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                Seleziona le foto da caricare
                            </Label>
                            <p className="mt-1 text-xs text-slate-500">
                                Formati: JPG, PNG, WEBP, HEIC · max 10 MB cad. · puoi selezionare anche centinaia di file insieme.
                            </p>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*,.heic,.heif"
                            onChange={(e) => handleBulkUpload(e.target.files)}
                            className="hidden"
                            data-testid="photos-files"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadBusy}
                            className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                            data-testid="photos-upload-btn"
                        >
                            {uploadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            Carica foto
                        </Button>
                    </div>

                    <div className={`mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 ${uploadProgress ? "" : "hidden"}`} data-testid="photos-progress">
                        <div className="flex items-center justify-between text-sm font-medium text-blue-900">
                            <span>
                                Lotto {uploadProgress?.batchIdx || 0} di {uploadProgress?.batches || 0}
                            </span>
                            <span className="font-mono-numbers">
                                {uploadProgress?.done || 0} / {uploadProgress?.total || 0}
                            </span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-100">
                            <div
                                className="h-full bg-[#376DB3] transition-all"
                                style={{ width: `${uploadProgress?.total ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%` }}
                            />
                        </div>
                    </div>

                    <div className={`mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 ${lastSummary && !uploadProgress ? "" : "hidden"}`}>
                        <b>{lastSummary?.assigned || 0}</b> assegnate · <b>{lastSummary?.pending || 0}</b> in attesa
                        <span className={(lastSummary?.errors || 0) > 0 ? "" : "hidden"}> · <b className="text-amber-800">{lastSummary?.errors || 0}</b> errori</span>
                    </div>
                </div>

                {/* Pending list */}
                <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">
                    Foto in attesa di assegnazione ({items.length})
                </h2>
                <div className={`flex items-center justify-center py-16 text-slate-500 ${loading ? "" : "hidden"}`}>
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <div
                    className={`rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center ${!loading && items.length === 0 ? "" : "hidden"}`}
                    data-testid="photos-empty"
                >
                    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                    <div className="mt-3 font-display text-lg font-semibold text-slate-700">Nessuna foto in attesa</div>
                    <div className="mt-1 text-sm text-slate-500">Carica nuove foto con il bottone qui sopra.</div>
                </div>
                <div
                    className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${!loading && items.length > 0 ? "" : "hidden"}`}
                    data-testid="photos-list"
                >
                    {items.map((photo) => (
                        <PhotoRow
                            key={photo.id}
                            photo={photo}
                            empOptions={empOptions}
                            busy={busyId === photo.id}
                            onAssign={confirmAssign}
                            onDelete={deletePending}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
}

const PhotoRow = ({ photo, empOptions, busy, onAssign, onDelete }) => {
    const [selected, setSelected] = useState(photo.suggested_employee_id || "");

    return (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`photo-row-${photo.id}`}>
            <div className="aspect-[3/4] bg-slate-100">
                <img src={photo.data_url} alt={photo.filename} className="h-full w-full object-cover" />
            </div>
            <div className="p-3">
                <div className="truncate text-xs font-mono text-slate-500" title={photo.filename}>{photo.filename}</div>
                <div className={`mt-1 text-xs text-indigo-700 ${photo.suggested_employee_name ? "" : "hidden"}`}>
                    Suggerito: <b>{photo.suggested_employee_name || ""}</b>
                    <span className={photo.suggested_score != null ? "" : "hidden"}>
                        {" "}({Math.round((photo.suggested_score || 0) * 100)}%)
                    </span>
                </div>
                <Select value={selected} onValueChange={setSelected}>
                    <SelectTrigger className="mt-2" data-testid={`photo-assign-${photo.id}`}>
                        <SelectValue placeholder="Seleziona dipendente" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                        {empOptions.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                                {e.label}{e.hasPhoto ? " · ha foto" : ""}{e.cessato ? " · CESSATO" : ""}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="mt-3 flex gap-2">
                    <Button
                        size="sm"
                        onClick={() => onAssign(photo, selected)}
                        disabled={!selected || busy}
                        className="flex-1 gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`photo-confirm-${photo.id}`}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                        Conferma
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDelete(photo)}
                        disabled={busy}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`photo-delete-${photo.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
